import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface UIState {
    showHeader: boolean;
    sidebarOpen: boolean;
    showAIAgent: boolean;
    currentPage: string;
    isLoading: boolean;
    notifications: Notification[];
    theme: 'light' | 'dark';
    breatheMode: boolean;
    flowState: {
        isActive: boolean;
        startTime: number | null;
        focusTime: number;
    };
    animations: {
        confetti: boolean;
        celebration: boolean;
        levelUp: boolean;
    };
}

interface Notification {
    id?: string;
    type: 'success' | 'error' | 'warning' | 'info';
    title?: string;
    message?: string;
    duration?: number;
    timestamp?: number;
}

const initialState: UIState = {
    showHeader: true,
    sidebarOpen: false,
    showAIAgent: false,
    currentPage: '',
    isLoading: false,
    notifications: [],
    theme: (localStorage.getItem('theme') as 'light' | 'dark' | undefined) || 'light',
    breatheMode: false,
    flowState: {
        isActive: false,
        startTime: null,
        focusTime: 0,
    },
    animations: {
        confetti: false,
        celebration: false,
        levelUp: false,
    },
};

const uiSlice = createSlice({
    name: 'ui',
    initialState,
    reducers: {
        toggleSidebar: (state) => {
            state.sidebarOpen = !state.sidebarOpen;
        },
        setSidebarOpen: (state, action: PayloadAction<boolean>) => {
            state.sidebarOpen = action.payload;
        },
        setShowAIAgent: (state, action: PayloadAction<boolean>) => {
            state.showAIAgent = action.payload;
        },
        setCurrentPage: (state, action: PayloadAction<string>) => {
            state.currentPage = action.payload;
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        addNotification: (state, action: PayloadAction<Omit<Notification, 'id' | 'timestamp'>>) => {
            const notification: Notification = {
                ...action.payload,
                id: Date.now().toString(),
                timestamp: Date.now(),
            };
            state.notifications.push(notification);
        },
        removeNotification: (state, action: PayloadAction<string>) => {
            state.notifications = state.notifications.filter((n) => n.id !== action.payload);
        },
        clearNotifications: (state) => {
            state.notifications = [];
        },
        setTheme: (state, action: PayloadAction<'light' | 'dark'>) => {
            state.theme = action.payload;
            localStorage.setItem('theme', action.payload);
        },
        setBreatheMode: (state, action: PayloadAction<boolean>) => {
            state.breatheMode = action.payload;
        },
        startFlowState: (state) => {
            state.flowState.isActive = true;
            state.flowState.startTime = Date.now();
        },
        stopFlowState: (state) => {
            state.flowState.isActive = false;
            state.flowState.startTime = null;
            state.flowState.focusTime = 0;
        },
        resetFlowState: (state: UIState) => {
            state.flowState = {
                isActive: false,
                startTime: null,
                focusTime: 0,
            };
        },
        updateFlowTime: (state, action: PayloadAction<number>) => {
            state.flowState.focusTime = action.payload;
        },
        triggerConfetti: (state) => {
            state.animations.confetti = true;
        },
        stopConfetti: (state) => {
            state.animations.confetti = false;
        },
        triggerCelebration: (state) => {
            state.animations.celebration = true;
        },
        stopCelebration: (state) => {
            state.animations.celebration = false;
        },
        triggerLevelUp: (state) => {
            state.animations.levelUp = true;
        },
        stopLevelUp: (state) => {
            state.animations.levelUp = false;
        },
        triggerAnimation: (state: UIState, action: PayloadAction<keyof typeof initialState.animations>) => {
            state.animations[action.payload] = true;
        },
        stopAnimation: (state: UIState, action: PayloadAction<keyof typeof initialState.animations>) => {
            state.animations[action.payload] = false;
        },
        setShowHeader: (state, action: PayloadAction<boolean>) => {
            state.showHeader = action.payload;
        },
    },
});

export const {
    toggleSidebar,
    setSidebarOpen,
    setShowAIAgent,
    setCurrentPage,
    setLoading,
    addNotification,
    removeNotification,
    clearNotifications,
    setTheme,
    setBreatheMode,
    startFlowState,
    stopFlowState,
    resetFlowState,
    updateFlowTime,
    triggerConfetti,
    stopConfetti,
    triggerCelebration,
    stopCelebration,
    triggerLevelUp,
    stopLevelUp,
    triggerAnimation,
    stopAnimation,
    setShowHeader,
} = uiSlice.actions;

export default uiSlice.reducer;
